import { WorkfrontSyncErrorStatus } from '@atlas/types'
import { EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Button, buttonVariants, Spinner } from '@atlas/ui-primitives'
import { Info } from 'lucide-react'
import { Link } from 'react-router-dom'
import { formatUtc } from '@/lib/format-utc'
import type { WorkfrontErrorDTO } from '@/services/workfront.service'
import { ErrorStatusBadge, humanize, SeverityBadge } from './StatusBadges'

export interface ErrorActions {
	onRetry: (error: WorkfrontErrorDTO) => void
	onIgnore: (error: WorkfrontErrorDTO) => void
}

export interface ErrorsTableProps extends ErrorActions {
	errors: WorkfrontErrorDTO[]
	isLoading?: boolean
	isError?: boolean
	hrefOf: (error: WorkfrontErrorDTO) => string
	/** What the empty state says; a list with filters says none match them. */
	emptyDescription?: string
}

/**
 * Fix, Retry and Ignore are offered only on an error still open: a retrying,
 * resolved or ignored one has nothing to act on. Fix opens the screen that fixes
 * a mapping error, pre-filled. Retry is offered only where re-running the steps
 * can fix it; otherwise the row says so, its fix one hover away.
 */
export function ErrorActionButtons({ error, onRetry, onIgnore }: ErrorActions & { error: WorkfrontErrorDTO }) {
	if (error.status !== WorkfrontSyncErrorStatus.OPEN) return null
	return (
		<span className="flex items-center justify-end gap-2">
			{error.fixLink ? (
				<Link
					to={error.fixLink}
					aria-label={`Fix: ${error.title}`}
					className={buttonVariants({ variant: 'outline', size: 'sm' })}
				>
					Fix
				</Link>
			) : null}
			{error.retryable ? (
				<Button size="sm" variant="outline" onPress={() => onRetry(error)}>
					Retry
				</Button>
			) : error.fixLink ? null : (
				<span
					className="inline-flex items-center gap-1 whitespace-nowrap text-ds-xs italic text-ds-text-subtle"
					title={error.fix}
				>
					<Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
					Retry won’t fix
				</span>
			)}
			<Button size="sm" variant="ghost" onPress={() => onIgnore(error)}>
				Ignore
			</Button>
		</span>
	)
}

/** One row per problem: what it is, where it happened, how many objects it touches, and when. */
function ErrorRow({ error, href, ...actions }: ErrorActions & { error: WorkfrontErrorDTO; href: string }) {
	return (
		<TableRow>
			<TableCell>
				<SeverityBadge severity={error.severity} />
			</TableCell>
			<TableCell>{humanize(error.step)}</TableCell>
			<TableCell>
				<Link to={href} className="font-medium hover:underline">
					{error.title}
				</Link>
			</TableCell>
			<TableCell>{error.affected.length}</TableCell>
			<TableCell>{formatUtc(error.firstSeenAt)}</TableCell>
			<TableCell>{formatUtc(error.lastSeenAt)}</TableCell>
			<TableCell>
				<ErrorStatusBadge status={error.status} severity={error.severity} />
			</TableCell>
			<TableCell>
				<ErrorActionButtons error={error} {...actions} />
			</TableCell>
		</TableRow>
	)
}

/** The adapter's readable errors, linking to their detail, with Retry and Ignore on the open ones. Prop-driven. */
export function ErrorsTable({
	errors,
	isLoading,
	isError,
	hrefOf,
	emptyDescription = 'None match these filters.',
	...actions
}: ErrorsTableProps) {
	if (isLoading) return <Spinner aria-label="Loading errors" />
	if (isError) return <EmptyState title="Could not load the errors" description="Try again in a moment." />
	if (errors.length === 0) return <EmptyState title="No errors" description={emptyDescription} />
	return (
		<Table aria-label="Errors">
			<TableHeader>
				<TableRow>
					{['Severity', 'Step', 'Problem', 'Affected', 'First seen', 'Last seen', 'Status', ''].map((column) => (
						<TableHead key={column || 'actions'}>{column}</TableHead>
					))}
				</TableRow>
			</TableHeader>
			<TableBody>
				{errors.map((error) => (
					<ErrorRow key={error.id} error={error} href={hrefOf(error)} {...actions} />
				))}
			</TableBody>
		</Table>
	)
}

/** One error opened: what happened, why it matters, how to fix it, and the objects it touches. */
export function ErrorDetailCard({ error, ...actions }: ErrorActions & { error: WorkfrontErrorDTO }) {
	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center gap-2">
				<SeverityBadge severity={error.severity} />
				<ErrorStatusBadge status={error.status} severity={error.severity} />
				<span className="text-ds-sm text-ds-text-muted">
					{humanize(error.step)} · {error.code} · seen {formatUtc(error.firstSeenAt)} to {formatUtc(error.lastSeenAt)}
				</span>
			</div>
			<h2 className="font-semibold text-ds-lg">{error.title}</h2>
			<section>
				<h3 className="font-medium">What happened</h3>
				<p className="text-ds-sm">{error.explanation}</p>
			</section>
			<section>
				<h3 className="font-medium">How to fix it</h3>
				<p className="text-ds-sm">{error.fix}</p>
			</section>
			{error.resolutionNote ? (
				<section>
					<h3 className="font-medium">Ignored because</h3>
					<p className="text-ds-sm">{error.resolutionNote}</p>
				</section>
			) : null}
			<section>
				<h3 className="font-medium">Affected ({error.affected.length})</h3>
				<pre className="overflow-x-auto rounded-ds-sm bg-ds-surface p-3 text-ds-xs">
					{JSON.stringify(error.affected, null, 2)}
				</pre>
			</section>
			<ErrorActionButtons error={error} {...actions} />
		</div>
	)
}
